import { default as SettingsService } from 'phoenix/services/settingsService';
import { 
	PhoenixFeature, 
	PhoenixFeatureStatus,
	PhoenixFeatureList,
	PhoenixUser,
	PhoenixUserGroup,
	PhoenixUserFeatures,
	PhoenixUserGroupFeatures,
	PhoenixDeploymentFeatures,
	PhoenixDeployment
} from 'phoenix/domain';

export class FeatureService {
	public static $inject = [
		'$http',
		'settingsService'
	];

	public global = this.createGlobalNamespace();
	public deployment = this.createDeploymentNamespace();
	public user = this.createUserNamespace();

	constructor(
		private $http:ng.IHttpService,
		private settingsService:SettingsService
	){
	}
	
	/**
	 * Gets the status of a feature for the current user.
	 */
	public getFeature(namespace:string, name:string){
		let uri = `${this.settingsService.serverAPIPath}/features/${namespace}/${name}`;
		return this.$http
			.get<PhoenixFeatureStatus>(uri)
			.then(resp => resp.data);
	}

	/**
	 * Gets a list of all active features for the current user.
	 */
	public getFeatures(){
		let uri = `${this.settingsService.serverAPIPath}/features/user/current`;
		return this.$http
			.get<PhoenixUserFeatures>(uri)
			.then(resp => resp.data.Features);
	}

	/**
	 * Toggle a feature based on its hypermedia link.
	 */
	public toggleFeature(feature:PhoenixFeature, enabled:boolean) {
		return this.setFeature(feature.Links.Toggle, enabled);
	}

	private createGlobalNamespace(){
		return {
			/**
			 * Toggles a feature on or off for all users in all groups in all deployments.
			 */
			toggle: (namespace:string, name:string, enabled:boolean) => {
				let url = `${this.settingsService.serverAPIPath}/features/${namespace}/${name}`;
				return this.setFeature(url, enabled);
			},
			/**
			 * Gets a list of all available features in the system.
			 */
			getFeatures: () => {
				let uri = `${this.settingsService.serverAPIPath}/features/all`;
				return this.$http
					.get<PhoenixFeatureList>(uri)
					.then(resp => resp.data.Features);
			}
		}
	}

	private createDeploymentNamespace(){
		return {
			/**
			 * Get all active features for a particular deployment.
			 */
			getFeatures: (deployment:PhoenixDeployment) => {
				let uri = `${this.settingsService.serverAPIPath}/features/deployment/${deployment.SystemName}`;
				return this.$http.get<PhoenixDeploymentFeatures>(uri).then(resp => resp.data);
			},		
			/**
			 * Toggle a feature for a particular deployment.
			 */
			toggle: (deployment:PhoenixDeployment, namespace:string, name:string, enabled:boolean) => {
				let url = `${this.settingsService.serverAPIPath}/features/deployment/${deployment.SystemName}/${namespace}/${name}`;		
				return this.setFeature(url, enabled);
			}
		};
	}

	private createUserGroupNamespace(){
		return {
			/**
			 * Get all active features for a particular user group.
			 */
			getFeatures: (userGroup:PhoenixUserGroup) => {
				let url = `${this.settingsService.serverAPIPath}/features/usergroup/${userGroup.UserGroupId}`;
				return this.$http.get<PhoenixUserGroupFeatures>(url).then(resp => resp.data);
			},
			/**
			 * Toggle a feature for a particular user group.
			 */
			toggle: (userGroup:PhoenixUserGroup, namespace:string, name:string, enabled:boolean) => {
				let url = `${this.settingsService.serverAPIPath}/features/usergroup/${userGroup.UserGroupId}/${namespace}/${name}`;
				return this.setFeature(url, enabled);
			}
		};
	}

	private createUserNamespace(){
		return {
			/**
			 * Get all active features for a particular user.
			 */
			getFeatures: (user:PhoenixUser) => {
				let url = `${this.settingsService.serverAPIPath}/features/user/${user.UserId}`;
				return this.$http.get<PhoenixUserFeatures>(url).then(resp => resp.data);
			},
			/**
			 * Toggle a feature for a particular user.
			 */
			toggle:(user:PhoenixUser, namespace:string, name:string, enabled:boolean) => {
				let url = `${this.settingsService.serverAPIPath}/features/user/${user.UserId}/${namespace}/${name}`;
				return this.setFeature(url, enabled);
			}
		};
	}

	/**
	 * Helper method to consolidate boilerplate for toggling.
	 */
	protected setFeature(url:string, enabled:boolean){
		let method = enabled ? 'PUT' : 'DELETE';
		return this.$http({
			method,
			url
		}).then(angular.noop);
	}
}



// WEBPACK FOOTER //
// ./src/phoenix/services/featureService.ts